import type { CareRequirement } from "@caredna-marlin/utils/client";
import { timezoneAbbreviation } from "@caredna-marlin/utils/dates";

export function getCareRequirementSummaryLabel(options: {
  index: number;
  visitTypeName?: string;
  firstVisitLabel?: string;
  laterVisitLabel?: string;
  emptyVisitTypeLabel?: string;
}): string {
  const {
    index,
    visitTypeName,
    firstVisitLabel = "First visit",
    laterVisitLabel = "Later",
    emptyVisitTypeLabel = "Select visit type",
  } = options;

  const prefix = index === 0 ? firstVisitLabel : laterVisitLabel;
  const visit = visitTypeName || emptyVisitTypeLabel;
  return `${prefix} - ${visit}`;
}

type TimeLabelFormatter = (
  date: Date,
  ampm?: boolean,
  overrideTimezone?: string | null,
) => string;

export function getCareRequirementDetailedSummaryLabel(options: {
  careRequirement: CareRequirement;
  index: number;
  visitTypeName?: string;
  timeLabel: TimeLabelFormatter;
  timezone?: string | null;
  emptyVisitTypeLabel?: string;
}): string {
  const {
    careRequirement,
    index,
    visitTypeName,
    timeLabel,
    timezone,
    emptyVisitTypeLabel = "Select visit type",
  } = options;

  const visit = visitTypeName || emptyVisitTypeLabel;
  if (careRequirement.scheduleType === "previousVisitRelative") {
    const minAfter = careRequirement.minHoursAfterLastVisit ?? 0;
    const maxAfter = careRequirement.maxHoursAfterLastVisit ?? 12;
    const clampedMin = Math.min(12, Math.max(0, minAfter));
    const clampedMax = Math.min(12, Math.max(0, maxAfter));
    const [rangeMin, rangeMax] = clampedMin <= clampedMax ? [clampedMin, clampedMax] : [clampedMax, clampedMin];
    const suffix = index === 0 ? "hours after last visit (previous day)" : "hours after last visit";
    return `${rangeMin}-${rangeMax} ${suffix} - ${visit}`;
  }

  const startLabel = timeLabel(careRequirement.minTime, true, timezone);
  const endLabel = timeLabel(careRequirement.maxTime, true, timezone);
  const zone = timezone ? ` ${timezoneAbbreviation(timezone)}` : "";
  return `${startLabel} - ${endLabel}${zone} - ${visit}`;
}

